/**
 * Noyau centralisé des formules pricing/remises/options.
 *
 * Source unique pour :
 * - Remises volume (paliers cumulatifs)
 * - Remise exceptionnelle
 * - Design-MOE (calcul jours + prix)
 * - Gestion de projet (calcul jours + prix)
 *
 * Consommé par :
 * - useProjectPricing (configurateur, sélection partielle)
 * - useSynthesisFinancials (synthèse, toutes missions)
 * - synthesisCalculations (calculs purs financiers)
 * - Navigation (affichage résumé)
 */

// ============================================================================
// TYPES
// ============================================================================

export type DisabledVolumeDiscounts = {
  "50k"?: boolean;
  "100k"?: boolean;
  "250k"?: boolean;
  "350k"?: boolean;
  "550k"?: boolean;
};

export interface VolumeDiscountResult {
  /** Montant total de la remise en euros */
  amount: number;
  /** Pourcentage total cumulé (ex: 25 pour 25%) */
  percentage: number;
}

export interface VolumeDiscountBreakdownItem {
  key: keyof DisabledVolumeDiscounts;
  threshold: number;
  rate: number;
  amount: number;
}

export interface DesignMoeResult {
  days: number;
  price: number;
}

export interface ManagementResult {
  days: number;
  price: number;
}

// ============================================================================
// PALIERS DE REMISES VOLUME
// ============================================================================

export const VOLUME_DISCOUNT_TIERS = [
  { threshold: 50000, rate: 0.02, key: "50k" as const },
  { threshold: 100000, rate: 0.03, key: "100k" as const },
  { threshold: 250000, rate: 0.04, key: "250k" as const },
  { threshold: 350000, rate: 0.06, key: "350k" as const },
  { threshold: 550000, rate: 0.10, key: "550k" as const },
] as const;

/**
 * Renvoie le détail des paliers volume activés.
 * Utile pour afficher les lignes de réduction tout en gardant une source unique des formules.
 */
export function computeVolumeDiscountBreakdown(
  subtotal: number,
  disabledVolumeDiscounts?: DisabledVolumeDiscounts
): VolumeDiscountBreakdownItem[] {
  if (subtotal <= 0) return [];

  const disabled = disabledVolumeDiscounts || {};
  const activeTiers: VolumeDiscountBreakdownItem[] = [];

  for (const tier of VOLUME_DISCOUNT_TIERS) {
    if (subtotal > tier.threshold && disabled[tier.key] !== true) {
      activeTiers.push({
        key: tier.key,
        threshold: tier.threshold,
        rate: tier.rate,
        amount: subtotal * tier.rate,
      });
    }
  }

  return activeTiers;
}

// ============================================================================
// REMISE VOLUME
// ============================================================================

/**
 * Calcule le montant et le pourcentage de remise volume.
 *
 * Les paliers sont **cumulatifs** : un subtotal de 600K€ active TOUS les paliers,
 * et chaque palier s'applique sur le subtotal original (pas sur le reste).
 */
export function computeVolumeDiscount(
  subtotal: number,
  disabledVolumeDiscounts?: DisabledVolumeDiscounts
): VolumeDiscountResult {
  const breakdown = computeVolumeDiscountBreakdown(subtotal, disabledVolumeDiscounts);
  const amount = breakdown.reduce((sum, tier) => sum + tier.amount, 0);
  const percentage = breakdown.reduce((sum, tier) => sum + tier.rate * 100, 0);
  return { amount, percentage };
}

/**
 * Raccourci : renvoie uniquement le pourcentage total de remise volume (entier).
 * Rétrocompatible avec l'ancien `calculateTotalDiscountPercentage` de formatUtils.
 */
export function computeVolumeDiscountPercentage(
  subtotal: number,
  disabledVolumeDiscounts?: DisabledVolumeDiscounts
): number {
  return computeVolumeDiscount(subtotal, disabledVolumeDiscounts).percentage;
}

// ============================================================================
// REMISE EXCEPTIONNELLE
// ============================================================================

/**
 * Calcule le montant de la remise exceptionnelle.
 */
export function computeExceptionalDiscount(
  subtotal: number,
  exceptionalDiscountPercentage: number | undefined | null
): number {
  if (!exceptionalDiscountPercentage || exceptionalDiscountPercentage <= 0 || subtotal <= 0) {
    return 0;
  }
  return subtotal * (exceptionalDiscountPercentage / 100);
}

// ============================================================================
// DESIGN-MOE
// ============================================================================

/**
 * Calcule les jours et le prix du Design-MOE.
 *
 * Formule : (baseDays + ceil(totalDays × percentage%)) × tjmMoe
 */
export function computeDesignMoe(
  totalDays: number,
  tjmMoe: number,
  baseDays: number = 3,
  percentage: number = 10
): DesignMoeResult {
  const normalizedTotalDays = Math.max(0, totalDays);
  const percentDays = Math.ceil(normalizedTotalDays * (percentage / 100));
  const days = baseDays + percentDays;
  return { days, price: days * tjmMoe };
}

// ============================================================================
// GESTION DE PROJET
// ============================================================================

/**
 * Calcule les jours et le prix de la gestion de projet.
 *
 * Formule : ceil(totalDays × percentage%) × tjmMoe
 */
export function computeProjectManagement(
  totalDays: number,
  tjmMoe: number,
  percentage: number = 10
): ManagementResult {
  if (totalDays <= 0) return { days: 0, price: 0 };
  const days = Math.ceil(totalDays * (percentage / 100));
  return { days, price: days * tjmMoe };
}
